import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, UserCheck, ChevronRight, ChevronDown, ArrowRight, Sparkles, ExternalLink, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSocialMeta } from "@/hooks/useSocialMeta";
import { getBlog, listApps, goUrl } from "@/lib/api";
import AdSlot from "@/components/AdSlot";
import { categoryColor } from "@/constants/categoryColors";

const BASE_URL = "https://horseappcompare.com";

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const [post, setPost] = useState(null);
  const [apps, setApps] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    getBlog(slug)
      .then(setPost)
      .catch(() => setNotFound(true));
    listApps({ limit: 100 }).then(setApps).catch(() => setApps([]));
  }, [slug]);

  const url = `${BASE_URL}/blog/${slug}`;
  useSocialMeta(post ? {
    title: post.title,
    description: post.meta_description,
    image: post.hero_image || post.image_url,
    type: "article",
    article: {
      published_time: post.published_at || post.created_at,
      modified_time: post.updated_at || post.published_at || post.created_at,
      section: post.category || "Horse apps",
      tags: post.tags || (post.referenced_slugs || []),
    },
  } : { title: "Blog | HorseAppCompare", description: "" });

  // Render an inline 404 instead of redirecting to /blog — a redirect causes
  // Emergent's SEO prerender to snapshot the /blog page and inherit its
  // canonical, which would point every missing article at /blog and confuse
  // Google. Keeping the canonical on this URL keeps indexing clean.
  if (notFound) {
    return (
      <main className="hac-container py-20 text-center" data-testid="blog-article-not-found">
        <h1 className="font-heading text-3xl text-[#0F172A] mb-3">Article not found</h1>
        <p className="text-[#64748B] mb-6">This blog article doesn't exist or was unpublished.</p>
        <Link to="/blog" className="hac-btn-primary">Back to the blog</Link>
      </main>
    );
  }
  if (!post) return <main className="hac-container py-20 text-center text-[#94A3B8]">…</main>;

  const date = new Date(post.published_at || post.created_at);
  const isNl = (post.language || lang) === "nl";
  const color = categoryColor(post.category);
  const referenced = (post.referenced_slugs || []).map((s) => apps.find((a) => a.slug === s)).filter(Boolean);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: post.title, item: url },
    ],
  };
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description,
    inLanguage: post.language,
    datePublished: post.published_at,
    dateModified: post.published_at || post.created_at,
    author: { "@type": "Organization", name: "HorseAppCompare" },
    publisher: { "@type": "Organization", name: "HorseAppCompare", url: BASE_URL },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
  const faqLd = post.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <main>
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-12 md:py-20 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[#2563EB]">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium line-clamp-1">{post.title}</span>
          </nav>
          <div className="overline mb-3 inline-flex items-center gap-2" style={{ color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {post.category || "general"}
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-[#0F172A] leading-[1.1] mb-5">
            {post.h1 || post.title}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl mb-6">{post.hero_lede}</p>
          <div className="flex flex-wrap items-center gap-5 text-sm text-[#64748B]">
            <span className="inline-flex items-center gap-2">
              <Calendar size={14} className="text-[#2563EB]" />
              {date.toLocaleDateString(isNl ? "nl-NL" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
            </span>
            <span className="inline-flex items-center gap-2">
              <UserCheck size={14} className="text-[#2563EB]" />
              {isNl ? "HorseAppCompare Redactie" : "HorseAppCompare Editorial"}
            </span>
          </div>
        </div>
      </section>

      <AdSlot placement="blog-banner" />

      <section className="hac-container py-12 max-w-3xl">
        <article
          className="prose prose-slate max-w-none
            prose-headings:font-heading prose-headings:text-[#0F172A] prose-headings:tracking-tight
            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-[#334155] prose-p:leading-relaxed
            prose-a:text-[#2563EB] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-[#0F172A]
            prose-li:text-[#334155]
            prose-ul:my-4"
          data-testid="blog-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_markdown}</ReactMarkdown>
        </article>

        {referenced.length > 0 && (
          <div className="mt-12 pt-10 border-t border-[#E2E8F0]" data-testid="blog-affiliate-block">
            <h3 className="font-heading text-xl text-[#0F172A] mb-1">
              {isNl ? "Probeer de apps uit dit artikel" : "Try the apps mentioned in this article"}
            </h3>
            <p className="text-sm text-[#64748B] mb-5">
              {isNl
                ? "Tracking-link met UTM-parameters — geen extra kosten voor jou, transparante doorklik-statistieken voor de aanbieders."
                : "Tracked link with UTM parameters — no extra cost to you, transparent click stats for the providers."}
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {referenced.map((a) => (
                <div
                  key={a.slug}
                  className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-xl bg-white hover:border-[#2563EB] transition-colors"
                  data-testid={`affiliate-row-${a.slug}`}
                >
                  <span
                    className="w-11 h-11 flex items-center justify-center text-white font-bold rounded-lg shrink-0"
                    style={{ backgroundColor: a.color || "#2563EB" }}
                  >
                    {a.name.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F172A] truncate">{a.name}</div>
                    <div className="text-xs text-[#94A3B8] inline-flex items-center gap-1">
                      <Star size={11} className="fill-[#2563EB] text-[#2563EB]" /> {a.rating?.toFixed(1) || "—"} · {a.country || ""}
                    </div>
                  </div>
                  <Link
                    to={`/apps/${a.slug}`}
                    className="text-xs text-[#64748B] hover:text-[#2563EB] px-2 py-1 whitespace-nowrap"
                    data-testid={`affiliate-review-${a.slug}`}
                  >
                    {isNl ? "Review" : "Review"}
                  </Link>
                  {a.website && (
                    <a
                      href={goUrl(a.slug, "blog", { article: slug, campaign: `blog-${post.category || "listicle"}` })}
                      target="_blank"
                      rel="noopener sponsored nofollow noreferrer"
                      className="hac-btn-primary text-xs whitespace-nowrap"
                      data-testid={`affiliate-cta-${a.slug}`}
                    >
                      {isNl ? `Open ${a.name}` : `Visit ${a.name}`} <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {post.faq?.length > 0 && (
          <div className="mt-12 pt-10 border-t border-[#E2E8F0]">
            <h3 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-6">
              {isNl ? "Veelgestelde vragen" : "Frequently asked questions"}
            </h3>
            <div className="space-y-3">
              {post.faq.map((f, i) => (
                <div key={i} className="border border-[#E2E8F0] rounded-xl bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-start justify-between gap-4 p-5 text-left"
                  >
                    <span className="font-bold text-[#0F172A]">{f.q}</span>
                    <ChevronDown
                      size={20}
                      className={`text-[#64748B] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openFaq === i && <div className="px-5 pb-5 text-[#64748B] leading-relaxed">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl p-8 text-center">
          <h3 className="font-heading text-2xl text-[#0F172A] mb-3">
            {isNl ? "Vergelijk meer paardenapps" : "Compare more horse apps"}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/best-horse-apps" className="hac-btn-primary">
              {isNl ? "Beste paardenapps gids" : "Best horse apps guide"} <ArrowRight size={14} />
            </Link>
            <Link to="/advisor" className="hac-btn-secondary">
              <Sparkles size={14} /> {isNl ? "Adviseur" : "Advisor"}
            </Link>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      {faqLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />}
    </main>
  );
};

export default BlogArticlePage;
